import React from 'react'

import { PixelTooltip } from 'pixel-base'
import styled from 'styled-components'

interface TruncatedTextWithTooltipProps {
  text?: string | null
  maxLength?: number
  className?: string
  style?: React.CSSProperties
  emptyPlaceholder?: string
}

export const TruncatedTextWithTooltip = ({
  text,
  maxLength,
  className,
  style,
  emptyPlaceholder = '--',
}: TruncatedTextWithTooltipProps) => {
  const textRef = React.useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = React.useState(false)

  const checkTruncation = React.useCallback(() => {
    const element = textRef.current

    if (!element) {
      return
    }

    const truncated = element.scrollWidth > element.clientWidth

    setIsTruncated((previous) =>
      previous === truncated ? previous : truncated,
    )
  }, [])

  React.useLayoutEffect(() => {
    checkTruncation()
  }, [checkTruncation, text, maxLength])

  React.useEffect(() => {
    const element = textRef.current

    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const observer = new ResizeObserver(() => {
      checkTruncation()
    })

    observer.observe(element)

    const parent = element.parentElement

    if (parent) {
      observer.observe(parent)
    }

    return () => {
      observer.disconnect()
    }
  }, [checkTruncation, text, maxLength])

  if (!text) {
    return (
      <TextCell>
        <TruncatedText className={className} style={style}>
          {emptyPlaceholder}
        </TruncatedText>
      </TextCell>
    )
  }

  const truncatedStyle: React.CSSProperties = {
    ...style,
    ...(maxLength ? { maxWidth: `min(100%, ${maxLength}ch)` } : {}),
  }

  return (
    <TextCell>
      <TruncatedText ref={textRef} className={className} style={truncatedStyle}>
        {text}
      </TruncatedText>
      {isTruncated && (
        <PixelTooltip
          tooltipText={text}
          tooltipPlacement="top"
          width="100%"
          style={{
            position: 'absolute',
            inset: 0,
            minWidth: 0,
            maxWidth: '100%',
            height: '100%',
          }}
        >
          <TooltipHitTarget tabIndex={0} aria-label={text} />
        </PixelTooltip>
      )}
    </TextCell>
  )
}

const TextCell = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
`

const TruncatedText = styled.span`
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const TooltipHitTarget = styled.span`
  position: absolute;
  inset: 0;
  display: block;
  cursor: default;
`
